import { useTraduccion } from '@tallerya/i18n';
import {
  AvisoError,
  Boton,
  CampoAreaTexto,
  CampoNumero,
  CampoSeleccion,
  CampoTexto,
  Fila,
  Formulario,
  IconoMas,
  Seccion,
  useAvisos,
  useFormulario,
  Ventana,
} from '@tallerya/ui';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { Adjuntos } from '../../componentes/adjuntos/Adjuntos';
import { useMarcas, useModelos } from '../marcas/datos';
import { VentanaMarca } from '../marcas/VentanaMarca';
import { VentanaModelo } from '../marcas/VentanaModelo';
import { useTipo, useTipos } from '../catalogos/datos';
import { aDetalle, aTexto, CamposDelTipo, erroresDelDetalle, nombreDe } from './CamposDelTipo';
import {
  useCambiosActivos,
  useCodigoPropuesto,
  useFichaActivo,
  useNombreDeCliente,
  version,
  type ActivoExistente,
  type FichaActivo,
  type ProblemaActivo,
} from './datos';
import { EliminarActivo } from './EliminarActivo';
import { SelectorDeCliente } from './SelectorDeCliente';

// Alta y edición de un activo (#119). **El formulario se arma solo**: al elegir
// el tipo aparecen debajo los campos que ese tipo declara (`CamposDelTipo`).
// Un tipo nuevo con sus campos genera su formulario sin tocar código.
//
//   /activos/nuevo                alta
//   /activos/nuevo?cliente=<id>   alta desde la ficha del cliente (#105)
//   /activos/:id/editar           edición, con la galería de fotos y archivos;
//                                 `/activos/:id` es la ficha (#187)
//
// No hay frame en el Figma: sigue el molde de los formularios de Sucursales y
// Clientes. El código viene propuesto y, si la persona no lo toca, se manda
// vacío: lo pone el sistema al guardar (decisión de producto, igual que en
// clientes). Marca y modelo se pueden crear ahí mismo, como «Nueva categoría»
// en Productos.

export function FormularioActivo() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const [parametros] = useSearchParams();
  const ficha = useFichaActivo(id);
  useTituloPantalla(id === undefined ? t('admin.activos.nuevo') : t('admin.activos.editar.titulo'));

  if (id === undefined) return <Editor clienteInicial={parametros.get('cliente')} />;

  if (ficha.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (ficha.isError || ficha.data === null) {
    const noExiste = ficha.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.activos.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.activos.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar('/activos')}>
          {t('admin.activos.volver')}
        </Boton>
      </section>
    );
  }

  // La clave lleva la versión: si la ficha cambia en la base y se vuelve a pedir,
  // el formulario arranca de nuevo con los valores frescos.
  return <Editor key={`${ficha.data.id}-${ficha.data.version}`} ficha={ficha.data} clienteInicial={null} />;
}

/**
 * El mismo formulario **en una ventana**, para registrar el carro sin salir de la
 * pantalla: el alta rápida de la orden (#131). Llega con la placa ya escrita y,
 * al guardar, devuelve el id del activo nuevo. Sin «Guardar y agregar otro» ni
 * galería de fotos: quien la abre está atendiendo un carro, no cargando varios.
 */
export function VentanaActivo({
  identificador,
  onCreado,
  onExistente,
  onCancelar,
}: {
  /** La placa que se escribió en la orden. Nulo con la ventana cerrada. */
  identificador: string | null;
  onCreado: (id: string) => void;
  /** «Usar ese activo →»: la placa ya la tenía otro activo (#147), y se elige ese. */
  onExistente: (existente: ActivoExistente) => void;
  onCancelar: () => void;
}) {
  if (identificador === null) return null;

  // La clave reinicia el formulario si se abre con otra placa.
  return (
    <Editor
      key={identificador}
      clienteInicial={null}
      ventana={{ identificador, onCreado, onExistente, onCancelar }}
    />
  );
}

interface EnVentana {
  identificador: string;
  onCreado: (id: string) => void;
  onExistente: (existente: ActivoExistente) => void;
  onCancelar: () => void;
}

/** Los valores del formulario: todo como texto, y el detalle como `details.<clave>`. */
function valoresIniciales(
  ficha: FichaActivo | undefined,
  clienteInicial: string | null,
  identificadorInicial = '',
): Record<string, string> {
  const valores: Record<string, string> = {
    assetTypeId: ficha ? String(ficha.assetTypeId) : '',
    code: ficha?.code ?? '',
    identifier: ficha?.identifier ?? identificadorInicial,
    customerId: ficha?.customerId ? String(ficha.customerId) : (clienteInicial ?? ''),
    brandId: ficha?.brandId ? String(ficha.brandId) : '',
    modelId: ficha?.modelId ? String(ficha.modelId) : '',
    year: ficha?.year != null ? String(ficha.year) : '',
    notes: ficha?.notes ?? '',
  };

  for (const [clave, valor] of Object.entries(ficha?.details ?? {})) {
    valores[nombreDe(clave)] = aTexto(valor);
  }

  return valores;
}

/** Un año que la API acepta: entero, entre `Asset.MinYear` y `Asset.MaxYear`. */
const anioValido = (texto: string) => /^\d{4}$/.test(texto) && Number(texto) >= 1900 && Number(texto) <= 2100;

function Editor({
  ficha,
  clienteInicial,
  ventana,
}: {
  ficha?: FichaActivo;
  clienteInicial: string | null;
  ventana?: EnVentana;
}) {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { avisar } = useAvisos();
  const cambios = useCambiosActivos();
  const editando = ficha !== undefined;
  // El id del activo recién creado, para devolverlo cuando la ventana se cierre.
  const creado = useRef<string | null>(null);
  // Con algo detrás se vuelve con «Atrás»: en el alta, la ficha del cliente
  // (#105); en la edición, la ficha del activo (#187) o el listado, con sus
  // filtros. Quien entró por un enlace va al listado limpio.
  const origen = useLocation().state as { desdeFicha?: boolean; desdeListado?: boolean } | null;
  const salir = () => void (origen?.desdeFicha || origen?.desdeListado ? navegar(-1) : navegar('/activos'));
  // Eliminado, su ficha ya no existe: se salta hasta lo que había detrás de ella
  // —`desdeListado` lo dice—, o se reemplaza esta pantalla por el listado limpio.
  const alEliminar = () =>
    void (editando && origen?.desdeFicha
      ? origen.desdeListado
        ? navegar(-2)
        : navegar('/activos', { replace: true })
      : salir());

  const [nuevaMarca, setNuevaMarca] = useState(false);
  const [nuevoModelo, setNuevoModelo] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [nombreCliente, setNombreCliente] = useState<string | null>(ficha?.customerName ?? null);

  const tipos = useTipos();
  const propuesto = useCodigoPropuesto(!editando).data;

  const form = useFormulario<Record<string, string>>({
    inicial: valoresIniciales(ficha, clienteInicial, ventana?.identificador),
    validar: (v) => {
      const errores: Record<string, string> = {};
      if (!v['assetTypeId']) errores['assetTypeId'] = 'validation.required';
      if (editando && !v['code']?.trim()) errores['code'] = 'validation.required';
      if (tipo.data?.requiresIdentifier && !v['identifier']?.trim()) errores['identifier'] = 'validation.required';
      if (v['year']?.trim() && !anioValido(v['year'].trim())) errores['year'] = 'validation.out_of_range';
      return { ...errores, ...erroresDelDetalle(v, campos) };
    },
    guardar: (v) => {
      const envio = {
        assetTypeId: v['assetTypeId'] ?? '',
        // El propuesto sin tocar se manda vacío: lo pone el sistema al guardar, y
        // dos altas a la vez no chocan por el mismo número.
        code: !editando && v['code']?.trim() === propuesto ? '' : (v['code'] ?? ''),
        identifier: v['identifier'] ?? '',
        customerId: v['customerId'] ?? '',
        brandId: v['brandId'] ?? '',
        modelId: v['modelId'] ?? '',
        year: v['year'] ?? '',
        notes: v['notes'] ?? '',
        details: aDetalle(v, campos),
      };
      return ficha
        ? cambios.editar(String(ficha.id), version(ficha.version), envio)
        : cambios.crear(envio, (id) => {
            creado.current = id;
          });
    },
    camposPorCodigo: {
      'asset_type.apagado': 'assetTypeId',
      'asset_type.no_encontrado': 'assetTypeId',
      'asset.codigo_tomado': 'code',
      'asset.correlativo_agotado': 'code',
      'asset.identificador_requerido': 'identifier',
      'asset.identificador_tomado': 'identifier',
      'asset.cliente_no_encontrado': 'customerId',
      'asset_brand.no_encontrada': 'brandId',
      'asset_brand.apagada': 'brandId',
      'asset.marca_de_otro_tipo': 'brandId',
      'asset_model.no_encontrado': 'modelId',
      'asset_model.apagado': 'modelId',
      'asset.modelo_de_otra_marca': 'modelId',
    },
    // La placa que ya tiene otro activo (#147): el aviso dice cuál y ofrece la
    // salida. En la página, ir a él; en la ventana del alta de una orden, usarlo:
    // es el carro que se buscaba, escrito con otro formato.
    completarAviso: (problema: ProblemaActivo) => {
      const existente = problema.code === 'asset.identificador_tomado' ? problema.existingAsset : null;
      if (!existente) return undefined;
      const id = String(existente.id);
      // En la ventana no se va al activo: se elige para la orden, con los avisos
      // que da la búsqueda (cliente inactivo), igual que si se lo hubiera buscado.
      return {
        texto: existente.code
          ? t('admin.activos.error.repetido', { identificador: existente.identifier, codigo: existente.code })
          : t('admin.activos.error.repetidoSinCodigo', { identificador: existente.identifier }),
        enlace: ventana
          ? { texto: t('admin.activos.error.usarExistente'), alHacerClic: () => ventana.onExistente(existente) }
          : {
              texto: t('admin.activos.error.verExistente'),
              // Reemplaza al formulario en el historial, como en Proveedores: «Atrás»
              // desde el existente no vuelve a un alta que ya no tiene sentido.
              alHacerClic: () => void navegar(`/activos/${id}`, { replace: true }),
            },
      };
    },
  });

  const tipoId = form.valores['assetTypeId'] ?? '';
  const marcaId = form.valores['brandId'] ?? '';
  const tipo = useTipo(tipoId || undefined);
  const campos = (tipo.data?.fields ?? []).filter((c) => c.isActive);

  // Las marcas que sirven para el tipo (las suyas y las genéricas), y los modelos
  // de la marca elegida. Lo que ya tenía el activo se ofrece aunque esté apagado.
  const marcas = useMarcas({ buscar: '', tipo: tipoId || 'todos', estado: 'activas' });
  const modelos = useModelos(marcaId || null);

  // **Con un solo tipo no hay nada que elegir** (#119). El taller automotriz nace
  // con «Vehículo» y nada más: el formulario lo deja puesto y no muestra el
  // selector, que sería un toque de más en cada alta. Vale también tras «Guardar
  // y agregar otro», que vacía el formulario.
  const tiposActivos = (tipos.data ?? []).filter((tp) => tp.isActive);
  const tipoUnico = !editando && tiposActivos.length === 1 ? tiposActivos[0] : undefined;
  const idTipoUnico = tipoUnico ? String(tipoUnico.id) : '';
  useEffect(() => {
    if (idTipoUnico && !form.valores['assetTypeId']) form.cambiar('assetTypeId', idTipoUnico);
  }, [idTipoUnico, form]);

  // El código propuesto llega después de abrir: se pone si la persona no
  // escribió otro.
  useEffect(() => {
    if (!editando && propuesto && !form.valores['code']) form.cambiar('code', propuesto);
  }, [editando, propuesto, form]);

  // Otro tipo, otras marcas: la elegida puede no servir. Otra marca, otros modelos.
  const tipoAnterior = useRef(tipoId);
  const marcaAnterior = useRef(marcaId);
  useEffect(() => {
    if (tipoAnterior.current !== tipoId) {
      tipoAnterior.current = tipoId;
      form.cambiar('brandId', '');
      form.cambiar('modelId', '');
    }
  }, [tipoId, form]);
  useEffect(() => {
    if (marcaAnterior.current !== marcaId) {
      marcaAnterior.current = marcaId;
      form.cambiar('modelId', '');
    }
  }, [marcaId, form]);

  // El nombre del cliente que llegó en la dirección, desde su ficha. Se calcula,
  // no se copia a un estado: vale mientras siga elegido ese mismo cliente.
  const nombreInicial = useNombreDeCliente(!editando && clienteInicial ? clienteInicial : null).data;
  const nombreMostrado =
    nombreCliente ?? (clienteInicial && form.valores['customerId'] === clienteInicial ? (nombreInicial ?? null) : null);

  const opcionesMarca = [
    ...(marcas.data ?? []).map((m) => ({ valor: String(m.id), etiqueta: m.name })),
    ...(ficha?.brandId && !(marcas.data ?? []).some((m) => m.id === ficha.brandId)
      ? [{ valor: String(ficha.brandId), etiqueta: ficha.brandName ?? '' }]
      : []),
  ];
  const opcionesModelo = (modelos.data ?? [])
    .filter((m) => m.isActive || m.id === ficha?.modelId)
    .map((m) => ({ valor: String(m.id), etiqueta: m.name }));
  const marcaElegida = (marcas.data ?? []).find((m) => String(m.id) === marcaId);

  function guardado(siguiente: 'volver' | 'otro') {
    avisar({ titulo: editando ? t('admin.activos.aviso.guardado') : t('admin.activos.aviso.creado') });
    if (siguiente === 'otro') {
      form.reiniciar(valoresIniciales(undefined, clienteInicial));
      return;
    }
    salir();
  }

  const etiquetaIdentificador = tipo.data?.identifierLabel ?? t('admin.activos.identificador');

  // Las secciones, iguales en la página y en la ventana.
  const secciones = (
    <>
      <Seccion titulo={t('admin.activos.seccion.general')} info={t('admin.activos.seccion.generalTexto')}>
        <Fila>
          {editando || tipoUnico ? (
            // El tipo no cambia: los detalles se validaron contra sus campos. Y
            // si el taller tiene uno solo, no hay entre qué elegir.
            <p className="activos__tipo-fijo">
              <span className="ui-campo__etiqueta">{t('admin.activos.tipo')}</span>
              <strong>{ficha?.assetTypeName ?? tipoUnico?.name}</strong>
            </p>
          ) : (
            <CampoSeleccion
              form={form}
              nombre="assetTypeId"
              etiqueta={t('admin.activos.tipo')}
              obligatorio
              placeholder={t('admin.activos.tipo.elegir')}
              opciones={tiposActivos.map((tp) => ({ valor: String(tp.id), etiqueta: tp.name }))}
            />
          )}
          <CampoTexto
            form={form}
            nombre="code"
            etiqueta={t('admin.activos.codigo')}
            obligatorio={editando}
            {...(!editando && { info: t('admin.activos.codigoAyuda') })}
            ancho="corto"
            autoComplete="off"
          />
        </Fila>
        {tipoId && (
          <Fila>
            <CampoTexto
              form={form}
              nombre="identifier"
              etiqueta={etiquetaIdentificador}
              obligatorio={tipo.data?.requiresIdentifier ?? false}
              autoComplete="off"
            />
            <CampoNumero form={form} nombre="year" etiqueta={t('admin.activos.anio')} decimales={0} enteros={4} sinMiles ancho="corto" />
          </Fila>
        )}
      </Seccion>

      {tipoId && (
        <Seccion titulo={t('admin.activos.seccion.marca')} info={t('admin.activos.seccion.marcaTexto')}>
          <Fila>
            <CampoSeleccion
              form={form}
              nombre="brandId"
              etiqueta={t('admin.activos.marca')}
              placeholder={t('admin.activos.sinMarca')}
              opciones={opcionesMarca}
            />
            <CampoSeleccion
              form={form}
              nombre="modelId"
              etiqueta={t('admin.activos.modelo')}
              placeholder={marcaId ? t('admin.activos.sinModelo') : t('admin.activos.modelo.antesMarca')}
              opciones={opcionesModelo}
            />
          </Fila>
          <div className="activos__al-vuelo">
            <Boton variante="texto" icono={<IconoMas />} onClick={() => setNuevaMarca(true)}>
              {t('admin.activos.nuevaMarca')}
            </Boton>
            <Boton
              variante="texto"
              icono={<IconoMas />}
              disabled={!marcaElegida}
              onClick={() => setNuevoModelo(true)}
            >
              {t('admin.activos.nuevoModelo')}
            </Boton>
          </div>
        </Seccion>
      )}

      <Seccion titulo={t('admin.activos.seccion.cliente')} info={t('admin.activos.seccion.clienteTexto')}>
        <SelectorDeCliente
          valor={form.valores['customerId'] ?? ''}
          nombre={nombreMostrado}
          error={form.errores['customerId']}
          id={form.idDe('customerId')}
          onElegir={(idCliente, nombre) => {
            form.cambiar('customerId', idCliente);
            setNombreCliente(nombre);
          }}
          onQuitar={() => {
            form.cambiar('customerId', '');
            setNombreCliente(null);
          }}
        />
      </Seccion>

      {tipoId && tipo.data && (
        <Seccion
          titulo={t('admin.activos.seccion.detalles')}
          info={t('admin.activos.seccion.detallesTexto', { tipo: tipo.data.name })}
        >
          {campos.length > 0 ? (
            <CamposDelTipo form={form} campos={campos} />
          ) : (
            <p className="activos__sin-campos">{t('admin.activos.sinCampos')}</p>
          )}
        </Seccion>
      )}

      <Seccion titulo={t('admin.activos.seccion.notas')}>
        <CampoAreaTexto form={form} nombre="notes" etiqueta={t('admin.activos.notas')} ancho="completo" />
      </Seccion>
    </>
  );

  const ventanasAlVuelo = (
    <>
      <VentanaMarca
        edicion={nuevaMarca ? { tipo: 'nueva' } : null}
        tipoInicial={tipoId}
        onCerrar={() => setNuevaMarca(false)}
        onCreada={(idMarca) => form.cambiar('brandId', idMarca)}
      />
      <VentanaModelo
        edicion={nuevoModelo && marcaElegida ? { tipo: 'nuevo', marca: marcaElegida } : null}
        onCerrar={() => setNuevoModelo(false)}
        onCreado={(idModelo) => form.cambiar('modelId', idModelo)}
      />
    </>
  );

  if (ventana) {
    return (
      <>
        <Ventana
          abierta
          titulo={t('admin.activos.nuevo')}
          ancho={720}
          acciones={
            <>
              <Boton variante="texto" disabled={form.guardando} onClick={ventana.onCancelar}>
                {t('app.cancelar')}
              </Boton>
              <Boton
                disabled={form.guardando}
                aria-busy={form.guardando}
                onClick={() =>
                  void form.enviar().then((bien) => {
                    if (bien && creado.current) {
                      avisar({ titulo: t('admin.activos.aviso.creado') });
                      ventana.onCreado(creado.current);
                    }
                  })
                }
              >
                {form.guardando ? t('ui.formulario.guardando') : t('app.guardar')}
              </Boton>
            </>
          }
        >
          {form.aviso && <AvisoError aviso={form.aviso} />}
          {secciones}
        </Ventana>
        {ventanasAlVuelo}
      </>
    );
  }

  return (
    <>
      <Formulario
        form={form}
        titulo={editando ? t('admin.activos.editar.titulo') : t('admin.activos.nuevo')}
        info={editando ? t('admin.activos.editar.info') : t('admin.activos.nuevo.descripcion')}
        {...(ficha && {
          descripcion: `${ficha.code ?? ''} · ${ficha.assetTypeName}${ficha.identifier ? ` · ${ficha.identifier}` : ''}`,
        })}
        {...(editando && { etiquetaGuardar: t('admin.activos.guardarCambios') })}
        guardarYOtro={!editando}
        onGuardado={guardado}
        onCancelar={salir}
        {...(ficha && {
          accionSecundaria: (
            <Boton variante="peligro" onClick={() => setEliminando(true)}>
              {t('admin.activos.eliminar.boton')}
            </Boton>
          ),
        })}
      >
        {secciones}
      </Formulario>

      {ficha && (
        <section className="activos__adjuntos" aria-label={t('admin.adjuntos.titulo')}>
          <Adjuntos origen={{ tipo: 'activo', id: String(ficha.id) }} />
        </section>
      )}

      {ventanasAlVuelo}
      <EliminarActivo
        activo={eliminando && ficha ? ficha : null}
        onCerrar={() => setEliminando(false)}
        onEliminado={alEliminar}
        // Desactivarlo cambia su versión: seguir en la edición daría un cambio
        // simultáneo al guardar. Se sale, a la ficha o al listado, que sí existen.
        onDesactivado={salir}
      />
    </>
  );
}
